import { AsyncPipe, DatePipe } from "@angular/common";
import { Component, Input, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TooltipPlacement } from "@app/app.constants";
import { BarrierListFilter, sortList } from "@app/models/filters/barrierListFilter";
import { PaginateFilter } from "@app/models/filters/paginateFilter";
import { Barrier, BarrierList } from "@app/models/barrier";
import { PaginateList, Pagination, Sorting } from "@app/models/paginateList";
import { BarrierService } from "@app/services/barrier.service";
import { TranslateModule } from "@ngx-translate/core";
import {
    ItListComponent,
    ItListItemComponent,
    ItTooltipDirective,
    ItDropdownComponent,
    ItDropdownItemComponent,
    ItPaginationComponent,
    ItNotificationService,
    ItModalComponent,
    ItButtonDirective,
    ItChipComponent,
} from "design-angular-kit";
import { MunicipalityNamePipe } from "@app/pipes/municipality.pipe";
import { QualificationType } from "@app/models/role";
import { AuthService } from "@app/services/auth.service";
import { FolderService } from "@app/services/folder.service";
import { Location } from "@angular/common";
import { States } from "@app/models/folder";
import { PurposeNamePipe } from "@app/pipes/purpose.pipe";

@Component({
    selector: "app-barrier-list",
    standalone: true,
    imports: [
        TranslateModule,
        DatePipe,
        ItListComponent,
        ItListItemComponent,
        ItTooltipDirective,
        ItDropdownComponent,
        ItDropdownItemComponent,
        ItPaginationComponent,
        FormsModule,
        AsyncPipe,
        MunicipalityNamePipe,
        ItModalComponent,
        ItButtonDirective,
        ReactiveFormsModule,
        ItChipComponent,
        PurposeNamePipe
    ],
    templateUrl: "./barrier-list.component.html",
})
export class BarrierListComponent implements OnInit {
    @ViewChild("updateLinkedFolderModal") updateLinkedFolderModal!: ItModalComponent;
    @Input() title: string | null = null;
    @Input() barrierId: number | null = null;
    @Input() barrierCode: string | null = null;
    @Input() data: Barrier[] | null = null;
    @Input() readonly: boolean = false;
    @Input() isPublic: boolean = false;
    readonly TooltipPlacement = TooltipPlacement;
    QualificationType = QualificationType;
    changerValue: number = 10;
    items: BarrierList = new BarrierList();
    sortList = sortList;
    pagination: Pagination | null = null;
    searchFilters: PaginateFilter<BarrierListFilter> = new PaginateFilter();
    selectedBarrier: Barrier | null = null;

    constructor(
        readonly barrierService: BarrierService,
        private folderService: FolderService,
        private router: Router,
        private readonly notificationService: ItNotificationService,
        readonly authService: AuthService,
        private location: Location,
    ) {}

    get indeterminate() {
        return !this.items.allBarriersChecked && !!this.items.checkedBarriers.length;
    }

    get selectedTranslationKey() {
        return `it.portal.barriers.selected.${this.items.checkedBarriers.length !== 1 ? "other" : "one"}`;
    }

    get toolsDisabled() {
        return false; // !this.items.checkedBarriers.length;
    }

    get isEmbedded() {
        return !!this.title || !!this.barrierCode || !!this.data;
    }

    ngOnChanges(changes: SimpleChanges) {
        for (let barrierName in changes) {
            switch (barrierName) {
                case "data":
                    if (this.data) this.items.risultati = this.data.map((item) => new Barrier(item));
                    break;
            }
        }
    }

    sortTranslationKey(item: Sorting) {
        return `it.portal.barriers.sort.${item.campo}-${item.verso.toLowerCase()}`;
    }

    isCurrentVersion(item: number) {
        return item === this.barrierId;
    }

    ngOnInit() {
        if (!this.data) {
            if (this.barrierCode) {
                this.searchFilters.filtri.codiceSbarramento = this.barrierCode;
                this.searchFilters.filtri.tutti = true;
            } else {
                this.barrierService.barrierList.subscribe((barrierList: PaginateList<Barrier>) => {
                    this.items.risultati = barrierList.risultati;
                    this.pagination = barrierList.paginazione;
                    this.searchFilters.ordinamento = barrierList.ordinamento;
                });
                this.barrierService.barrierListFilter.subscribe((barrierListFilter: BarrierListFilter) => {
                    this.searchFilters.filtri = barrierListFilter;
                });
            }
            this.searchFilters.ordinamento = sortList[0];
            this.getBarriers();
        } else this.items.risultati = this.data.map((item) => new Barrier(item));
    }

    getBarriers() {
        this.barrierService.getAll(this.searchFilters).subscribe({
            next: (res) => {
                this.items.risultati = res.risultati;
                this.pagination = res.paginazione;
                this.barrierService.setBarrierList(res);
            },
        });
    }

    openBarrier(id: number) {
        window.location.href = this.location.prepareExternalUrl(`sbarramenti/sbarramento/${id}`);
    }

    openFolder(id: number) {
        this.router.navigate([`/fascicoli/fascicolo/${id}`]);
    }

    createFolder(id: number) {
        this.folderService.create(id).subscribe({
            next: (res) => {
                this.notificationService.success(res.titolo, res.messaggio);
                this.getBarriers();
            },
        });
    }

    download(type: string) {
        this.barrierService.export(this.searchFilters, type).subscribe({
            next: (res) => {
                if (res.body) {
                    const url = URL.createObjectURL(res.body);
                    const a = document.createElement("a");
                    a.href = url;
                    const contentDisposition = res.headers.get("Content-Disposition");
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^"]+)"?/);
                        if (match && match[1]) a.download = match[1];
                    }
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }
            },
        });
    }

    deleteBarrier(id: number) {
        this.barrierService.delete(id).subscribe({
            next: (res) => {
                this.notificationService.success(res.titolo, res.messaggio);
                this.getBarriers();
            },
        });
    }

    createVersion(barrier: Barrier, updateFolder?: boolean) {
        this.selectedBarrier = barrier;
        if (
            updateFolder === undefined &&
            barrier.fascicolo &&
            (barrier.statoFascicolo === States.DRAFT || barrier.statoFascicolo === States.ASSIGNMENT)
        ) {
            this.updateLinkedFolderModal.show();
            return;
        }

        this.selectedBarrier &&
            this.barrierService.createVersion(this.selectedBarrier.uid, undefined, updateFolder).subscribe({
                next: (res) => {
                    this.notificationService.success(res.titolo, res.messaggio);
                    this.getBarriers();
                    this.updateLinkedFolderModal.hide();
                },
            });
    }

    onSortOptionChange(item: Sorting) {
        this.searchFilters.ordinamento = item;
        this.getBarriers();
    }

    onPageChange(pageNumber: number) {
        scrollTo(0, 0);
        this.searchFilters.paginazione.numeroPagina = pageNumber;
        this.getBarriers();
    }

    onChangerChange(value: number): void {
        this.searchFilters.paginazione = { numeroPagina: 0, elementiPagina: value };
        this.getBarriers();
    }
}
